import React, { useEffect, useState } from "react";
import {
  Grid,
  Button,
  Divider,
  Box,
  Stack,
  Icon,
  Typography,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  CircularProgress,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import PODetailHeader from "../../molecules/PurchaseOrder/PODetailHeader";
import { useLocation } from "react-router-dom";
import DashboardLayout from "../../layouts/DashboardLayout";
import { updatePurchaseOrder } from "../../api/purchaseOrders";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { useCompany } from "../../contexts/CompanyContext";
import PODetailTableService from "../../organisms/PurchaseOrders/PODetailTableService";
import { AssetModal } from "../../molecules/Assets";
import LGModal from "../../molecules/LGModal";
import PurchaseOrderForm from "../../organisms/PurchaseOrders/PurchaseOrderForm";
import {
  buildDefaultItems,
  createHandleApprove,
  PDF_OPTIONS,
  usePODetailCatalogs,
  usePODetailPdfMenu,
} from "./poDetailShared";

const PODetailServiceScreen = () => {
  const location = useLocation();
  const { openSnack } = useSnack();
  const POdata = location.state || {};
  const id = POdata.id;
  const formMode = POdata.items?.length > 0 ? "edit" : "insert";
  const { selectedCompany } = useCompany();
  const [isSaved, setIsSaved] = useState(false);
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [headerModalOpen, setHeaderModalOpen] = useState(false);

  const { expenseAccounts, units, catalogsLoading } = usePODetailCatalogs(
    POdata,
    selectedCompany?.id,
  );
  const {
    pdfMenuOpen,
    selectedPdfIndex,
    pdfAnchorRef,
    handlePdfClick,
    handlePdfMenuItemClick,
    handlePdfToggle,
    handlePdfClose,
  } = usePODetailPdfMenu(id, POdata, openSnack);

  const handleApprove = createHandleApprove(id, openSnack);

  const initialValues = {
    purchaseOrder: { ...POdata },
    items: buildDefaultItems(POdata),
  };

  const handleAssetCreated = (newAsset) => {
    openSnack(`Activo "${newAsset.name}" creado exitosamente`, "success");
  };

  const handleSubmit = async (values) => {
    const payload = { ...values };

    payload.items = payload.items.map((e) => {
      const { id: itemId, __rowId, isNew, name, ...itemWithoutId } = e;
      return {
        ...itemWithoutId,
        productId:
          itemWithoutId.productId && itemWithoutId.productId != 0
            ? itemWithoutId.productId
            : null,
      };
    });

    delete payload.purchaseOrder.id;
    try {
      await updatePurchaseOrder(id, payload);
      openSnack("Órden de compra guardada exitosamente", "success");
      setIsSaved(true);
    } catch (e) {
      openSnack(handleError(e), "error");
    }
  };

  useEffect(() => {
    if (
      formMode === "edit" &&
      !catalogsLoading &&
      expenseAccounts.length > 0 &&
      units.length > 0 &&
      POdata.items?.length > 0
    ) {
      initialValues.items = POdata.items;
    }
  }, [formMode, catalogsLoading, expenseAccounts.length, units.length, POdata.items]);

  if (!selectedCompany) {
    return (
      <DashboardLayout>
        <Box p={4}>
          <Typography>Seleccione una compañía primero</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box p={2}>
        {catalogsLoading ? (
          <CircularProgress />
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape({})}
            onSubmit={handleSubmit}
          >
            {({ values }) => (
              <Form>
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      position: "sticky",
                      top: 50,
                      backgroundColor: "background.default",
                      zIndex: 1000,
                      paddingTop: 2,
                      paddingBottom: 2,
                    }}
                  >
                    <PODetailHeader />
                    <Divider sx={{ marginTop: 2 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      onClick={() => setHeaderModalOpen(true)}
                      startIcon={<Icon>edit</Icon>}
                    >
                      Modificar ODC
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <LGModal
                      open={headerModalOpen}
                      handleClose={() => setHeaderModalOpen(false)}
                      header="Editar orden de compra"
                    >
                      <PurchaseOrderForm id={id} companyId={selectedCompany?.id} />
                    </LGModal>
                  </Grid>

                  <Grid item xs={12}>
                    <PODetailTableService
                      items={values.items}
                      expenseAccounts={expenseAccounts}
                      units={units}
                    />
                  </Grid>

                  <Grid item xs={12} spacing={2}>
                    <Stack direction="row" spacing={2}>
                      <Button type="submit" variant="contained" color="primary">
                        Guardar
                      </Button>
                      <Button
                        variant="contained"
                        disabled={!isSaved}
                        onClick={handleApprove}
                        color="success"
                      >
                        Aprobar
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setAssetModalOpen(true)}
                        startIcon={<Icon>add</Icon>}
                      >
                        Agregar Activo
                      </Button>
                      <Box>
                        <ButtonGroup variant="contained" color="primary" ref={pdfAnchorRef}>
                          <Button disabled={!isSaved} onClick={handlePdfClick}>
                            <Icon>{PDF_OPTIONS[selectedPdfIndex].icon}</Icon>
                            {PDF_OPTIONS[selectedPdfIndex].label}
                          </Button>
                          <Button
                            size="small"
                            aria-controls={pdfMenuOpen ? "pdf-split-menu" : undefined}
                            aria-expanded={pdfMenuOpen ? "true" : undefined}
                            aria-haspopup="menu"
                            onClick={handlePdfToggle}
                            disabled={!isSaved}
                          >
                            <Icon>arrow_drop_down</Icon>
                          </Button>
                        </ButtonGroup>
                        <Popper
                          sx={{ zIndex: 1300 }}
                          open={pdfMenuOpen}
                          anchorEl={pdfAnchorRef.current}
                          role={undefined}
                          transition
                          disablePortal
                        >
                          {({ TransitionProps, placement }) => (
                            <Grow
                              {...TransitionProps}
                              style={{
                                transformOrigin:
                                  placement === "bottom" ? "center top" : "center bottom",
                              }}
                            >
                              <Paper>
                                <ClickAwayListener onClickAway={handlePdfClose}>
                                  <MenuList id="pdf-split-menu" autoFocusItem>
                                    {PDF_OPTIONS.map((option, index) => (
                                      <MenuItem
                                        key={option.label}
                                        selected={index === selectedPdfIndex}
                                        onClick={(event) => handlePdfMenuItemClick(event, index)}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </MenuList>
                                </ClickAwayListener>
                              </Paper>
                            </Grow>
                          )}
                        </Popper>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        )}
      </Box>

      <AssetModal
        open={assetModalOpen}
        onClose={() => setAssetModalOpen(false)}
        onAssetCreated={handleAssetCreated}
        companyId={selectedCompany?.id}
      />
    </DashboardLayout>
  );
};

export default React.memo(PODetailServiceScreen);
